import { useState } from 'react';
import { Outlet } from 'react-router';

import { SkipLink } from '@/components/SkipLink';
import { Sidebar } from '@/components/admin/Sidebar';
import { Topbar } from '@/components/admin/Topbar';

/**
 * The admin shell: fixed sidebar beside a scrolling content column.
 *
 * Below 1024px the sidebar becomes a slide-over. design-system.md §6 — and the
 * employee portal will use this same shell rather than a bottom tab bar, so
 * there is one navigation model to maintain rather than two.
 */
export function AdminLayout() {
  /*
   * Closing on navigation is the Sidebar's job, through the `onNavigate` it is
   * handed below — not an effect watching the pathname. Same result, and it does
   * not set state during render, which React 19 flags: an effect that fires on
   * every navigation to set a value that is almost always already false is a
   * render pass nobody needed.
   */
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-ink-50">
      <SkipLink />
      {/* Fixed 224px, desktop only. */}
      <aside className="fixed inset-y-0 left-0 hidden w-56 border-r border-ink-300 bg-white lg:block">
        <Sidebar />
      </aside>

      {menuOpen ? (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
            aria-hidden="true"
            onClick={() => {
              setMenuOpen(false);
            }}
          />
          <aside
            className="fixed inset-y-0 left-0 z-40 w-64 border-r border-ink-300 bg-white lg:hidden"
            // A dialog rather than a nav landmark while it is a slide-over:
            // it traps the page behind it, and saying so is what lets a screen
            // reader treat it as something to be dismissed.
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <Sidebar
              onNavigate={() => {
                setMenuOpen(false);
              }}
            />
          </aside>
        </>
      ) : null}

      <div className="lg:pl-56">
        <Topbar
          onOpenMenu={() => {
            setMenuOpen(true);
          }}
        />
        <main id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
